import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaChevronLeft,
  FaChevronRight,
  FaCompress,
  FaExpand,
  FaPause,
  FaPlay,
  FaRegImages,
  FaTimes,
} from "react-icons/fa";

const slides = [
  {
    src: "/images/MACTournoi/01.png",
    alt: "Accueil",
    caption: "Accueil de l’application",
    description: "Une entrée claire vers les espaces de gestion du tournoi.",
  },
  {
    src: "/images/MACTournoi/02.png",
    alt: "Création Compétition et options",
    caption: "Création de la compétition",
    description: "Paramétrage de la compétition, des options et de l’organisation générale.",
  },
  {
    src: "/images/MACTournoi/03.png",
    alt: "Page Compétition et Menus",
    caption: "Page compétition",
    description: "Accès rapide aux menus essentiels pour piloter l’événement.",
  },
  {
    src: "/images/MACTournoi/04.png",
    alt: "Pesées / Présences",
    caption: "Pesées & présences",
    description: "Contrôle des combattants présents et ajustements avant répartition.",
  },
  {
    src: "/images/MACTournoi/05.png",
    alt: "Répartition des Compétiteurs",
    caption: "Répartition des compétiteurs",
    description: "Création des poules, tableaux et répartitions selon les catégories.",
  },
  {
    src: "/images/MACTournoi/06.png",
    alt: "Gestion des Combats",
    caption: "Gestion des combats",
    description: "Suivi des combats sous forme de poules sur les différents tapis.",
  },
  {
    src: "/images/MACTournoi/07.png",
    alt: "Tableau de Score Table",
    caption: "Table de marque",
    description: "Scoreboard intégré et désignation du vainqueur pour la table d’arbitrage.",
  },
  {
    src: "/images/MACTournoi/11.png",
    alt: "Tableau de Score tapis",
    caption: "Affichage tapis",
    description: "Tableau de score public pour l’affichage sur le tapis.",
  },
  {
    src: "/images/MACTournoi/08.png",
    alt: "Résultats de la Compétition",
    caption: "Résultats de la compétition",
    description: "Consultation des résultats à la fin ou pendant l’avancement du tournoi.",
  },
  {
    src: "/images/MACTournoi/08a.png",
    alt: "Résultats de la Compétition pour les Podiums",
    caption: "Podiums par poules",
    description: "Résultats par poules et préparation des podiums.",
  },
  {
    src: "/images/MACTournoi/09.png",
    alt: "Statistiques",
    caption: "Statistiques",
    description: "Indicateurs et données utiles pour analyser la compétition.",
  },
  {
    src: "/images/MACTournoi/10.png",
    alt: "Résultats Publics et Coach",
    caption: "Résultats publics",
    description: "Suivi en temps réel pour les coachs, clubs et visiteurs.",
  },
];

const clampIndex = (value, length) => {
  if (!length) return 0;
  return ((value % length) + length) % length;
};

const DiaporamaTournoi = ({
  autoPlay = true,
  interval = 5500,
  showThumbnails = true,
}) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const containerRef = useRef(null);
  const thumbsRef = useRef(null);
  const touchRef = useRef({ x: 0, y: 0, active: false });

  const current = slides[index];
  const total = slides.length;

  const progressPercent = useMemo(() => {
    if (!total) return 0;
    return ((index + 1) / total) * 100;
  }, [index, total]);

  const goTo = useCallback((nextIndex) => {
    setLoaded(false);
    setIndex(clampIndex(nextIndex, slides.length));
  }, []);

  const next = useCallback(() => {
    goTo(index + 1);
  }, [goTo, index]);

  const prev = useCallback(() => {
    goTo(index - 1);
  }, [goTo, index]);

  useEffect(() => {
    if (!autoPlay || paused || lightboxOpen) return undefined;

    const id = setInterval(() => {
      setIndex((currentIndex) => clampIndex(currentIndex + 1, slides.length));
      setLoaded(false);
    }, interval);

    return () => clearInterval(id);
  }, [autoPlay, paused, lightboxOpen, interval]);

  useEffect(() => {
    const preload = (targetIndex) => {
      const img = new Image();
      img.src = slides[clampIndex(targetIndex, slides.length)].src;
    };

    preload(index + 1);
    preload(index - 1);
  }, [index]);

 useEffect(() => {
  const thumbsContainer = thumbsRef.current;
  if (!thumbsContainer) return;

  const activeThumb = thumbsContainer.querySelector(
    `[data-thumb-index="${index}"]`
  );

  if (!activeThumb) return;

  const containerWidth = thumbsContainer.clientWidth;
  const thumbLeft = activeThumb.offsetLeft;
  const thumbWidth = activeThumb.offsetWidth;

  const targetLeft = thumbLeft - containerWidth / 2 + thumbWidth / 2;

  thumbsContainer.scrollTo({
    left: Math.max(0, targetLeft),
    behavior: "smooth",
  });
}, [index]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const isFocusedInside = containerRef.current?.contains(
        document.activeElement
      );

      if (!isFocusedInside && !lightboxOpen) return;

      if (event.key === "ArrowRight") next();
      if (event.key === "ArrowLeft") prev();

      if (event.key === "Escape" && lightboxOpen) {
        setLightboxOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [next, prev, lightboxOpen]);

  const onTouchStart = (event) => {
    const touch = event.changedTouches[0];
    touchRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      active: true,
    };
  };

  const onTouchEnd = (event) => {
    if (!touchRef.current.active) return;

    const touch = event.changedTouches[0];
    const dx = touch.clientX - touchRef.current.x;
    const dy = touch.clientY - touchRef.current.y;

    touchRef.current.active = false;

    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 45) {
      dx < 0 ? next() : prev();
    }
  };

  return (
    <>
      <div
        ref={containerRef}
        tabIndex={0}
        className="relative mx-auto max-w-[1280px] outline-none"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-2xl shadow-slate-950/10 dark:border-white/10 dark:bg-slate-950/70 sm:rounded-[2.5rem]">
          <div className="pointer-events-none absolute -left-20 -top-20 h-72 w-72 rounded-full bg-blue-400/15 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -right-20 h-72 w-72 rounded-full bg-orange-400/15 blur-3xl" />

          <div className="relative grid gap-0 lg:grid-cols-[1fr_330px]">
            <div className="relative bg-slate-950 p-2 sm:p-3 lg:p-4">
              <div className="relative overflow-hidden rounded-[1.5rem] bg-slate-900">
                <div className="absolute left-4 top-4 z-20 hidden items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-white backdrop-blur sm:flex">
                  <FaRegImages className="h-3.5 w-3.5 text-orange-300" />
                  Aperçu application
                </div>

                <div className="flex aspect-[4/3] w-full items-center justify-center bg-gradient-to-br from-slate-900 via-slate-950 to-slate-800 md:aspect-[16/10] xl:aspect-[16/9]">
                  {!loaded && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="h-12 w-12 animate-spin rounded-full border-4 border-white/15 border-t-orange-400" />
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => setLightboxOpen(true)}
                    className="group h-full w-full cursor-zoom-in"
                    aria-label="Agrandir l’image"
                  >
                    <img
                      key={current.src}
                      src={current.src}
                      alt={current.alt}
                      loading="lazy"
                      onLoad={() => setLoaded(true)}
                      className={`h-full w-full object-contain transition-all duration-500 ${
                        loaded
                          ? "scale-100 opacity-100"
                          : "scale-[0.985] opacity-0"
                      }`}
                    />
                  </button>
                </div>

                <button
                  type="button"
                  aria-label="Image précédente"
                  onClick={prev}
                  className="absolute left-2 top-1/2 z-20 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-2xl border border-white/15 bg-white/10 text-white shadow-xl backdrop-blur transition hover:-translate-x-0.5 hover:bg-orange-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 sm:left-4 sm:h-12 sm:w-12"
                >
                  <FaChevronLeft className="h-4 w-4" />
                </button>

                <button
                  type="button"
                  aria-label="Image suivante"
                  onClick={next}
                  className="absolute right-2 top-1/2 z-20 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-2xl border border-white/15 bg-white/10 text-white shadow-xl backdrop-blur transition hover:translate-x-0.5 hover:bg-orange-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 sm:right-4 sm:h-12 sm:w-12"
                >
                  <FaChevronRight className="h-4 w-4" />
                </button>

                <button
                  type="button"
                  onClick={() => setLightboxOpen(true)}
                  className="absolute bottom-4 right-4 z-20 hidden h-11 w-11 items-center justify-center rounded-2xl border border-white/15 bg-white/10 text-white backdrop-blur transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 sm:inline-flex"
                  aria-label="Afficher en grand"
                  title="Afficher en grand"
                >
                  <FaExpand className="h-4 w-4" />
                </button>
              </div>
            </div>

            <aside className="relative flex flex-col justify-between border-t border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.03] sm:p-6 lg:border-l lg:border-t-0">
              <div>
                <div className="mb-4 inline-flex items-center rounded-full bg-orange-50 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-orange-700 dark:bg-orange-500/10 dark:text-orange-300">
                  Étape {index + 1} / {total}
                </div>

                <h3 className="text-2xl font-bold leading-tight text-slate-950 dark:text-white">
                  {current.caption}
                </h3>

                <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                  {current.description}
                </p>
              </div>

              <div className="mt-6">
                <div className="mb-3 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-blue-600 to-orange-500 transition-all duration-500"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => setPaused((value) => !value)}
                    className="inline-flex min-h-11 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-800 shadow-sm transition hover:-translate-y-0.5 hover:border-orange-200 hover:text-orange-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 dark:border-white/10 dark:bg-slate-950/50 dark:text-white dark:hover:border-orange-400/40 dark:hover:text-orange-300"
                    aria-label={
                      paused
                        ? "Reprendre le diaporama"
                        : "Mettre en pause le diaporama"
                    }
                  >
                    {paused ? (
                      <>
                        <FaPlay className="h-3.5 w-3.5" />
                        Lecture
                      </>
                    ) : (
                      <>
                        <FaPause className="h-3.5 w-3.5" />
                        Pause
                      </>
                    )}
                  </button>

                  <div className="flex items-center gap-2">
                    {slides.map((slide, slideIndex) => (
                      <button
                        key={`${slide.src}-dot`}
                        type="button"
                        onClick={() => goTo(slideIndex)}
                        aria-label={`Aller à l’image ${slideIndex + 1}`}
                        className={`h-2.5 rounded-full transition-all ${
                          slideIndex === index
                            ? "w-8 bg-orange-500"
                            : "w-2.5 bg-slate-300 hover:bg-slate-400 dark:bg-white/20 dark:hover:bg-white/40"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>

          {showThumbnails && (
            <div className="border-t border-slate-200 bg-white/80 px-3 py-3 dark:border-white/10 dark:bg-slate-950/50 sm:px-4">
              <div
                ref={thumbsRef}
                className="flex snap-x items-center gap-2 overflow-x-auto overflow-y-hidden pb-1"
              >
                {slides.map((slide, slideIndex) => {
                  const active = slideIndex === index;

                  return (
                    <button
                      key={slide.src}
                      type="button"
                      data-thumb-index={slideIndex}
                      onClick={() => goTo(slideIndex)}
                      className={`group relative shrink-0 snap-center overflow-hidden rounded-2xl border p-1 transition ${
                        active
                          ? "border-orange-400 bg-orange-50 shadow-md dark:bg-orange-500/10"
                          : "border-slate-200 bg-slate-50 hover:border-orange-200 dark:border-white/10 dark:bg-white/[0.04] dark:hover:border-orange-400/40"
                      }`}
                      aria-label={`Voir ${slide.caption}`}
                      title={slide.caption}
                    >
                      <img
                        src={slide.src}
                        alt={slide.alt}
                        loading="lazy"
                        className={`h-14 w-24 rounded-xl object-cover transition sm:h-16 sm:w-28 ${
                          active
                            ? "opacity-100"
                            : "opacity-70 group-hover:opacity-100"
                        }`}
                      />

                      <span
                        className={`absolute left-2 top-2 flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-bold ${
                          active
                            ? "bg-orange-500 text-white"
                            : "bg-slate-950/70 text-white"
                        }`}
                      >
                        {slideIndex + 1}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/90 p-3 backdrop-blur-sm sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-label="Aperçu agrandi du diaporama"
        >
          <div className="relative flex h-full w-full max-w-[1500px] flex-col">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-orange-300">
                  {index + 1} / {total}
                </p>
                <h3 className="truncate text-lg font-bold text-white sm:text-2xl">
                  {current.caption}
                </h3>
              </div>

              <button
                type="button"
                onClick={() => setLightboxOpen(false)}
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/10 text-white transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300"
                aria-label="Fermer l’aperçu"
              >
                <FaTimes className="h-4 w-4" />
              </button>
            </div>

            <div
              className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-[2rem] border border-white/10 bg-black"
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
            >
              <img
                src={current.src}
                alt={current.alt}
                className="h-full w-full object-contain"
              />

              <button
                type="button"
                aria-label="Image précédente"
                onClick={prev}
                className="absolute left-3 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-2xl border border-white/10 bg-white/10 text-white backdrop-blur transition hover:bg-orange-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 sm:h-12 sm:w-12"
              >
                <FaChevronLeft className="h-4 w-4" />
              </button>

              <button
                type="button"
                aria-label="Image suivante"
                onClick={next}
                className="absolute right-3 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-2xl border border-white/10 bg-white/10 text-white backdrop-blur transition hover:bg-orange-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 sm:h-12 sm:w-12"
              >
                <FaChevronRight className="h-4 w-4" />
              </button>

              <button
                type="button"
                onClick={() => setLightboxOpen(false)}
                className="absolute bottom-4 right-4 hidden h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-white/10 text-white backdrop-blur transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 sm:inline-flex"
                aria-label="Réduire l’image"
                title="Réduire"
              >
                <FaCompress className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DiaporamaTournoi;